/**
 * Client-side cache of persisted per-tile alert lifecycle (S12). Backed by
 * the `DashboardUiState` sidecar's `alertState` field via
 * `getUiState`/`setUiState` — the SAME per-key deep-merge (C1) semantics
 * apply server-side, so writes from this store never clobber a concurrent
 * patch to a DIFFERENT tile's alert state.
 *
 * Loaded lazily per dashboard (`ensureLoaded`) so "fire-once-until-cleared"
 * survives an app restart: the evaluator must never run against an empty
 * baseline before the persisted state has actually arrived — that would
 * re-notify for an alert that was already firing before the app closed.
 */
import { create } from 'zustand';
import type { TileAlertState } from '../types/dashboardTypes';

type DashboardAlertStoreState = {
  byDashboard: Record<string, Record<string, TileAlertState>>;
  loaded: Set<string>;
  loading: Map<string, Promise<void>>;
  ensureLoaded: (dashboardId: string) => Promise<void>;
  setTileState: (dashboardId: string, tileId: string, state: TileAlertState) => Promise<void>;
};

const api = () => window.electronAPI?.dashboards;

export const useDashboardAlertStore = create<DashboardAlertStoreState>((set, get) => ({
  byDashboard: {},
  loaded: new Set(),
  loading: new Map(),

  ensureLoaded: async (dashboardId) => {
    if (get().loaded.has(dashboardId)) return;
    const inFlight = get().loading.get(dashboardId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const bridge = api();
      const uiState = bridge ? await bridge.getUiState(dashboardId) : { version: 1 as const };
      set((s) => ({
        byDashboard: { ...s.byDashboard, [dashboardId]: uiState.alertState ?? {} },
        loaded: new Set(s.loaded).add(dashboardId),
      }));
    })();

    set((s) => ({ loading: new Map(s.loading).set(dashboardId, promise) }));
    await promise;
  },

  setTileState: async (dashboardId, tileId, state) => {
    set((s) => ({
      byDashboard: {
        ...s.byDashboard,
        [dashboardId]: { ...(s.byDashboard[dashboardId] ?? {}), [tileId]: state },
      },
    }));
    const bridge = api();
    if (!bridge) return;
    await bridge.setUiState(dashboardId, { alertState: { [tileId]: state } });
  },
}));

/** Read helper for non-React callers. */
export const getTileAlertState = (
  dashboardId: string,
  tileId: string,
): TileAlertState | undefined => useDashboardAlertStore.getState().byDashboard[dashboardId]?.[tileId];
